import {useAtom} from 'jotai';
import {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useMyCoupons} from '@/api/coupon';
import {couponLoginAlertAtom} from '@/atom';
import {Button} from '@/components/Button';
import {Modal} from '@/components/Modal';
import {Typography} from '@/components/Typography';
import {
  COUPON_EVENTS,
  COUPON_LOGIN_ALERT_SESSION_KEY,
  COUPON_MODAL_OPEN_PARAM,
} from '@/constants';
import {useSession} from '@/hooks';
import {useGA4WithParams} from '@/hooks/tracking';
import {paths} from '@/router';
import {palette} from '@/styles';
import {hasActiveUsePass} from '@/utils';

interface IProps {
  availableCount: number;
  onClose(): void;
}

export const CouponLoginAlertModal = ({availableCount, onClose}: IProps) => {
  const navigate = useNavigate();
  const tracking = useGA4WithParams();

  const isTracked = useRef<boolean>(false);

  useEffect(() => {
    if (isTracked.current) {
      return;
    }

    isTracked.current = true;
    tracking(COUPON_EVENTS.loginAlertView, {available_count: availableCount});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMove = () => {
    tracking(COUPON_EVENTS.loginAlertClick, {available_count: availableCount});
    onClose();
    navigate(`${paths.pricing_root}?${COUPON_MODAL_OPEN_PARAM}=1`);
  };

  // 우측 상단 닫기(X)는 알림만 닫는다.
  const handleClose = () => {
    tracking(COUPON_EVENTS.loginAlertClose, {available_count: availableCount});
    onClose();
  };

  return (
    <Modal title="보유 쿠폰 안내" width={520} onClose={handleClose}>
      <Body>
        <Typography variant="bold18" align="center" color={palette.gray800}>
          지금 바로 쓸 수 있는 쿠폰이 있어요!
        </Typography>
        <Typography variant="medium14" align="center" color={palette.gray600}>
          결제 없이 바로 시작할 수 있는 보유 쿠폰이 있어요.
          <br />
          지금 확인하고 사용해 보세요.
        </Typography>
      </Body>
      <ButtonArea>
        <Button
          variant="fill"
          color="primary"
          size="large"
          fullWidth
          onClick={handleMove}
        >
          쿠폰 확인하러 가기
        </Button>
      </ButtonArea>
    </Modal>
  );
};

/**
 * 로그인 직후 자동 노출 알림.
 * 이용 중인 이용권·쿠폰이 없고 유효한 미사용 보유 쿠폰이 1개 이상일 때 세션당 1회만 노출한다.
 */
export const CouponLoginAlertSystem = () => {
  const {hasSession, user, isPaidUser} = useSession();
  const {data: coupons, isSuccess} = useMyCoupons(user?.id);
  // 판정 결과는 atom 에 둔다 — 재마운트(개발 StrictMode, 라우트 이동)로 노출 대기 상태가 사라지지 않게.
  const [alert, setAlert] = useAtom(couponLoginAlertAtom);

  const availableCount = coupons?.length ?? 0;
  const hasActivePass = hasActiveUsePass(coupons, isPaidUser);

  useEffect(() => {
    const userId = user?.id;

    // 로그아웃하면 판정을 버린다 — 새로고침 없이 다시 로그인해도 재판정되도록.
    if (!hasSession || !userId) {
      if (alert) {
        setAlert(null);
      }
      return;
    }

    // 로그인 직후 1회만 판정한다. 이후 쿠폰을 등록해 보유 수가 늘어도 다시 뜨지 않는다.
    if (!isSuccess || alert?.userId === userId) {
      return;
    }

    const sessionKey = `${COUPON_LOGIN_ALERT_SESSION_KEY}:${userId}`;

    // 세션당 1회 — 노출하지 않기로 판정한 경우에도 기록해 재판정하지 않는다.
    if (sessionStorage.getItem(sessionKey)) {
      setAlert({userId, state: 'done'});
      return;
    }

    const eligible = !hasActivePass && availableCount > 0;

    sessionStorage.setItem(sessionKey, 'true');
    setAlert({userId, state: eligible ? 'visible' : 'done'});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, user?.id, isSuccess, alert]);

  if (alert?.state !== 'visible') {
    return null;
  }

  return (
    <CouponLoginAlertModal
      availableCount={availableCount}
      onClose={() => setAlert({userId: user?.id ?? '', state: 'done'})}
    />
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 24px;
`;

const ButtonArea = styled.div`
  padding: 0 24px 24px;
`;
